<template>
  <el-dialog :visible.sync="content.show" class="request-dialog RequestAccountModal" :close-on-press-escape="false" :close-on-click-modal="false" @close="cancel" show-header="false">
    <div class="request-container">
      <!-- Header -->
      <div class="request-header">
        <div class="header-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <div>
          <div class="request-title">Create your account</div>
          <div class="request-desc">Fill in the details below to get started.</div>
        </div>
      </div>

      <el-form ref="requestForm" :model="form" :rules="rules" class="request-form">
        <!-- Section: Personal Info -->
        <div class="section-label">Personal Info</div>

        <div class="grid-3">
          <el-form-item label="Prefix" prop="Prefix">
            <el-select v-model="form.Prefix" placeholder="—" class="input-field" clearable>
              <el-option v-for="opt in prefixOptions" :key="opt" :label="opt" :value="opt" />
            </el-select>
          </el-form-item>

          <el-form-item label="First Name" prop="FirstName" class="required-field">
            <el-input v-model.trim="form.FirstName" placeholder="e.g. Jane" class="input-field" />
          </el-form-item>

          <el-form-item label="Middle Name" prop="MiddleName">
            <el-input v-model.trim="form.MiddleName" placeholder="Optional" class="input-field" />
          </el-form-item>
        </div>

        <div class="grid-2-narrow">
          <el-form-item label="Last Name" prop="LastName" class="required-field">
            <el-input v-model.trim="form.LastName" placeholder="e.g. Smith" class="input-field" />
          </el-form-item>

          <el-form-item label="Suffix" prop="Suffix">
            <el-select v-model="form.Suffix" placeholder="—" class="input-field" clearable>
              <el-option v-for="opt in suffixOptions" :key="opt" :label="opt" :value="opt" />
            </el-select>
          </el-form-item>
        </div>

        <div class="section-divider" />

        <!-- Section: Contact & Organization -->
        <div class="section-label">Contact & Organization</div>

        <el-form-item label="Organization" prop="Organization">
          <el-input v-model.trim="form.Organization" placeholder="Your company or institution" class="input-field" />
        </el-form-item>

        <div class="grid-2">
          <el-form-item label="Email Address" prop="Email" class="required-field">
            <el-input v-model.trim="form.Email" placeholder="you@example.com" class="input-field" />
          </el-form-item>

          <el-form-item label="Phone Number" prop="PhoneNumber">
            <el-input v-model.trim="form.PhoneNumber" placeholder="+1 555 000 0000" class="input-field" />
          </el-form-item>
        </div>

        <div class="grid-2">
          <el-form-item label="Country" prop="Country">
            <el-select v-model="form.Country" placeholder="Select Country" class="input-field" clearable>
              <el-option v-for="opt in countryOptions" :key="opt" :label="opt" :value="opt" />
            </el-select>
          </el-form-item>

          <el-form-item label="Job Role" prop="JobRole">
            <el-select v-model="form.JobRole" placeholder="Select Job Role" class="input-field" clearable>
              <el-option v-for="opt in jobRoleOptions" :key="opt" :label="opt" :value="opt" />
            </el-select>
          </el-form-item>
        </div>

        <!-- Notes Textarea -->
        <!-- <el-form-item label="Notes" prop="Notes" class="required-field"> -->
        <el-form-item label="Notes" prop="Notes">
          <el-input type="textarea" v-model.trim="form.Notes" placeholder="Enter notes..." rows="4" />
        </el-form-item>

      </el-form>

      <!-- Success Banner -->
      <transition name="fade">
        <div v-if="successVisible" class="success-banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Account request submitted successfully!
        </div>
      </transition>

      <!-- Footer -->
      <div class="dialog-footer">
        <el-button class="cancel-btn" @click="cancel">Cancel</el-button>
        <el-button type="primary" class="submit-btn" :loading="submitLoading" :disabled="submitLoading" @click="submit">
          Submit request
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { weblogin, newuseremail } from "@/api";
import { storageName } from "@/utils/storageName";
export default {
  name: "CreateAccountForm",
  data() {
    return {
      content: { show: true },
      submitLoading: false,
      successVisible: false,
      form: {
        Prefix: "",
        FirstName: "",
        MiddleName: "",
        LastName: "",
        Suffix: "",
        Organization: "",
        Email: "",
        PhoneNumber: "",
        Country: "",
        JobRole: "",
        Notes: "", // 新增 Notes
      },
      prefixOptions: ["Mr.", "Ms.", "Dr.", "Prof.", "Mx."],
      suffixOptions: ["Jr.", "Sr.", "II", "III", "IV"],
      countryOptions: [
        "Afghanistan",
        "Albania",
        "Algeria",
        "Andorra",
        "Angola",
        "Antigua and Barbuda",
        "Argentina",
        "Armenia",
        "Australia",
        "Austria",
        "Azerbaijan",
        "Bahamas (The)",
        "Bahrain",
        "Bangladesh",
        "Barbados",
        "Belarus",
        "Belgium",
        "Belize",
        "Benin",
        "Bhutan",
        "Bolivia (Plurinational State of)",
        "Bosnia and Herzegovina",
        "Botswana",
        "Brazil",
        "Brunei Darussalam",
        "Bulgaria",
        "Burkina Faso",
        "Burundi",
        "Cabo Verde",
        "Cambodia",
        "Cameroon",
        "Canada",
        "Central African Republic (the)",
        "Chad",
        "Chile",
        "China",
        "Colombia",
        "Comoros (the)",
        "Congo (the)",
        "Costa Rica",
        "Côte d'Ivoire",
        "Croatia",
        "Cuba",
        "Cyprus",
        "Czechia",
        "Democratic People's Republic of Korea (the)",
        "Democratic Republic of the Congo (the)",
        "Denmark",
        "Djibouti",
        "Dominica",
        "Dominican Republic (the)",
        "Ecuador",
        "Egypt",
        "El Salvador",
        "Equatorial Guinea",
        "Eritrea",
        "Estonia",
        "Eswatini",
        "Ethiopia",
        "Fiji",
        "Finland",
        "France",
        "Gabon",
        "Gambia (the)",
        "Georgia",
        "Germany",
        "Ghana",
        "Greece",
        "Grenada",
        "Guatemala",
        "Guinea",
        "Guinea-Bissau",
        "Guyana",
        "Haiti",
        "Honduras",
        "Hungary",
        "Iceland",
        "India",
        "Indonesia",
        "Iran (Islamic Republic of)",
        "Iraq",
        "Ireland",
        "Israel",
        "Italy",
        "Jamaica",
        "Japan",
        "Jordan",
        "Kazakhstan",
        "Kenya",
        "Kiribati",
        "Kuwait",
        "Kyrgyzstan",
        "Lao People's Democratic Republic (the)",
        "Latvia",
        "Lebanon",
        "Lesotho",
        "Liberia",
        "Libya",
        "Liechtenstein",
        "Lithuania",
        "Luxembourg",
        "Madagascar",
        "Malawi",
        "Malaysia",
        "Maldives",
        "Mali",
        "Malta",
        "Marshall Islands (the)",
        "Mauritania",
        "Mauritius",
        "Mexico",
        "Micronesia (Federated States of)",
        "Monaco",
        "Mongolia",
        "Montenegro",
        "Morocco",
        "Mozambique",
        "Myanmar",
        "Namibia",
        "Nauru",
        "Nepal",
        "Netherlands (Kingdom of the)",
        "New Zealand",
        "Nicaragua",
        "Niger (the)",
        "Nigeria",
        "North Macedonia",
        "Norway",
        "Oman",
        "Pakistan",
        "Palau",
        "Panama",
        "Papua New Guinea",
        "Paraguay",
        "Peru",
        "Philippines (the)",
        "Poland",
        "Portugal",
        "Qatar",
        "Republic of Korea (the)",
        "Republic of Moldova (the)",
        "Romania",
        "Russian Federation (the)",
        "Rwanda",
        "Saint Kitts and Nevis",
        "Saint Lucia",
        "Saint Vincent and the Grenadines",
        "Samoa",
        "San Marino",
        "Sao Tome and Principe",
        "Saudi Arabia",
        "Senegal",
        "Serbia",
        "Seychelles",
        "Sierra Leone",
        "Singapore",
        "Slovakia",
        "Slovenia",
        "Solomon Islands",
        "Somalia",
        "South Africa",
        "South Sudan",
        "Spain",
        "Sri Lanka",
        "Sudan (the)",
        "Suriname",
        "Sweden",
        "Switzerland",
        "Syrian Arab Republic (the)",
        "Tajikistan",
        "Thailand",
        "Timor-Leste",
        "Togo",
        "Tonga",
        "Trinidad and Tobago",
        "Tunisia",
        "Türkiye",
        "Turkmenistan",
        "Tuvalu",
        "Uganda",
        "Ukraine",
        "United Arab Emirates (the)",
        "United Kingdom of Great Britain and Northern Ireland (the)",
        "United Republic of Tanzania (the)",
        "United States of America (the)",
        "Uruguay",
        "Uzbekistan",
        "Vanuatu",
        "Venezuela (Bolivarian Republic of)",
        "Viet Nam",
        "Yemen",
        "Zambia",
        "Zimbabwe"
      ],
      jobRoleOptions: [
        "Cardiologist",
        "Endocrinologist",
        "Gastroenterologist",
        "Genetic Counselor",
        "Geneticist",
        "Healthcare Staff Member",
        "Hematologist",
        "Immunologist",
        "Nephrologist",
        "Neurologist",
        "Nurse",
        "Oncologist",
        "Other Physician Specialist"
      ],
      rules: {
        FirstName: [{ required: true, message: "Required", trigger: "blur" }],
        LastName: [{ required: true, message: "Required", trigger: "blur" }],
        Email: [{ required: true, message: "Required", trigger: "blur" }, { type: "email", message: "Please enter a valid email", trigger: ["blur", "change"] }],
        // Notes: [{ required: true, message: "Required", trigger: "blur" }]
      },
    };
  },

  methods: {
    async submit() {
      this.$refs.requestForm.validate(async (valid) => {
        if (!valid) return;
        this.submitLoading = true;

        try {
          // 1. 获取 token
          const loginRes = await weblogin({ dbtable: 'autoEmail' });
          if (loginRes.data.error !== 0) throw new Error("Login failed");

          const token = loginRes.data.result;

          this.$useSession.setStorage(storageName.TokenName, token);
          // 2. 调用 newuseremail
          const params = { ...this.form };
          const emailRes = await newuseremail(params);
          if (emailRes.data.error === 0) {
            this.$message({
              message: "Sent successfully. Please check your email.",
              type: "success",
              duration: 3000
            });
            this.successVisible = true;
            setTimeout(() => {
              this.successVisible = false;
              this.cancel();
            }, 2000);
          } else {
            this.$errorplugIin("Submission failed");
          }

        } catch (err) {
          console.error(err);
          this.$errorplugIin("Request failed");
        } finally {
          this.submitLoading = false;
        }
      });
    },

    cancel() {
      // this.content.show = false;
      // this.submitLoading = false;
      // this.successVisible = false;
      // this.$refs.requestForm && this.$refs.requestForm.resetFields();
    },
  },
};
</script>

<style lang="scss" scoped>
/* 保留原有样式，未修改 */
::v-deep .el-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
}
::v-deep .el-dialog__body {
  padding: 0;
}
.request-container {
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
}
.request-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.header-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e8f0fe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #1a73e8;
}
.request-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}
.request-desc {
  font-size: 13px;
  color: #888;
  margin-top: 2px;
}
.request-form {
  padding: 20px 28px 0;
}
.section-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #aaa;
  margin-bottom: 12px;
}
.section-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 16px 0 20px;
}
.grid-3 {
  display: grid;
  grid-template-columns: 110px 1fr 1fr;
  gap: 12px;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.grid-2-narrow {
  display: grid;
  grid-template-columns: 1fr 90px;
  gap: 12px;
}
.input-field {
  width: 100%;
}
::v-deep .el-input__inner,
::v-deep .el-select .el-input__inner {
  height: 36px;
  line-height: 36px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  color: #1a1a1a;
  transition: border-color 0.2s, background 0.2s;
  &::placeholder {
    color: #bbb;
  }
  &:hover {
    border-color: #bbb;
    background: #fff;
  }
  &:focus {
    border-color: #1a73e8;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.1);
  }
}
::v-deep .el-form-item__label {
  font-size: 12px;
  color: #555;
  line-height: 1.4;
  padding-bottom: 4px;
  font-weight: 500;
}
.required-field ::v-deep .el-form-item__label::after {
  content: " *";
  color: #e53935;
}
::v-deep .el-form-item__error {
  font-size: 11px;
  color: #e53935;
  padding-top: 3px;
}
::v-deep .el-form-item.is-error .el-input__inner,
::v-deep .el-form-item.is-error .el-select .el-input__inner {
  border-color: #e53935;
  background: #fff8f8;
}
.success-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 28px 16px;
  padding: 10px 14px;
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  font-size: 13px;
  color: #2e7d32;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 28px 24px;
  border-top: 1px solid #f0f0f0;
  margin-top: 20px;
}
.cancel-btn {
  height: 36px;
  padding: 0 18px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #444;
  transition: background 0.2s;
  &:hover {
    background: #ebebeb;
    border-color: #ccc;
  }
}
.submit-btn {
  height: 36px;
  padding: 0 20px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  background: #1a73e8;
  border-color: #1a73e8;
  color: #fff;
  transition: background 0.2s, box-shadow 0.2s;
  &:hover {
    background: #1558d6;
    border-color: #1558d6;
    box-shadow: 0 2px 8px rgba(26, 115, 232, 0.3);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
<style  lang='scss'>
.el-select-dropdown__item {
  padding: 0 20px !important;
}
</style>


// WEBPACK FOOTER //
// src/components/form/RequestAccountModal.vue